import React, { useEffect, useState } from 'react';
import { Guide } from '../../../../types/opportunity-pov-items';
import {
  Typography,
} from '@mui/material';
import { fetchItems } from '../../../../support/queries';
import AIContent from '../../../Common/AIContent';
import { AIResponse } from '../../../../types/aiTypes';
import { createAIResponse, fetchOrCreateAIContent } from '../../../../support/aiHelpers';
import { HbrDivider, HbrSpinner } from '@harbor/elements-react-standalone';
import { useFetchPov } from '../../opportunityDetailHelpers';
import { OpportunityProduct } from '../../../../types/opportunity';
import { useParams } from 'react-router-dom';
import { useFetchOpportunity, useFetchOpportunityProductAssignment } from '../../../../hooks/opportunity-hooks';
import { useScreenSize } from '../../../../hooks/useScreenSize';

 
const ValidatePhaseReadout: React.FC<{ selectedProductId: OpportunityProduct['id'] }> = ({ selectedProductId }) => {
  const { id: opportunityId } = useParams();
  const { data: opportunity } = useFetchOpportunity(opportunityId);
  const { data: selectedProduct } = useFetchOpportunityProductAssignment(selectedProductId, opportunityId, { enabled: !!opportunityId && !!selectedProductId });

  const productName = selectedProduct?.product?.name || '';
  const [guide, setGuide] = useState<Guide>(null);
  const [aiContextText, setAiContextText] = useState<string>('');
  const [aiResponse, setAIResponse] = useState<AIResponse | null>(null);
  const [aiLoading, setAILoading] = useState(false);
  const [aiError, setAIError] = useState<string | null>(null);
  const { data: pov } = useFetchPov(opportunity);
  const { height: viewportHeight } = useScreenSize();

// Adjust this number to match your page header/margins above AIContent
  const AI_TOP_OFFSET_PX = 510;

// Ensure a reasonable minimum
  const aiContentHeightPx = Math.max(320, viewportHeight - AI_TOP_OFFSET_PX);
  const aiContentHeight = `${aiContentHeightPx}px`;


  useEffect(() => {
    if (guide && selectedProduct) {
      generateAIDesignContext();
    }
    // eslint-disable-next-line
  }, [guide, selectedProduct]);

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        if (pov) {
          const povOutcomes = await fetchItems('db/pov-outcome', { pov: pov.id });
          const enrichedOutcomes = await Promise.all(
            povOutcomes.map(async (outcome) => {
              const povUseCases = await fetchItems('db/pov-usecase', { pov_outcome: outcome.id });
              const useCases = await Promise.all(
                povUseCases.map(async (useCase) => {
                  const testCases = await fetchItems('db/pov-test-case', { pov_usecase: useCase.id });
                  return { ...useCase, testCases };
                })
              );
              return { ...outcome, useCases };
            })
          );
          const guidesWithDetails = {
            id: `guide-${pov.id}`,
            title: pov.name,
            outcomes: enrichedOutcomes,
            povId: pov.id,
          };

          if (guidesWithDetails) {
            setGuide(guidesWithDetails);
          }
        }
      } catch (error) {
        console.error('Error fetching or creating guides:', error);
      }
    };

    fetchGuides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunity]);

  const fetchOrCreateAIGuide = async (context) => {
    setAIError(null);
    setAIResponse(null);
    setAILoading(true);

    try {
      // 1. Try to fetch or create AI content
      let chat = await fetchOrCreateAIContent(
        'opportunity',
        opportunityId,
        108, // question_id for validation/deployment
        context,
        productName,
        [],
        opportunity
      );

      // 2. If not found/valid, explicitly create it
      if (!chat || typeof chat !== 'object' || !('id' in chat)) {
        chat = await createAIResponse(context, 'opportunity', opportunityId, 108, productName, []);
      }

      if (chat && typeof chat === 'object' && 'id' in chat) {
        setAIResponse(chat as AIResponse);
      } else {
        setAIError('No PoV Readout could be created.');
      }
    } catch (error) {
      setAIError('Error fetching PoV Readout.');
    } finally {
      setAILoading(false);
    }
  };

  const generateAIDesignContext = () => {
    if (!guide) {
      alert('No Guide object has been set');
      return;
    }
    let context = `Proof of Value Product: ${selectedProduct.product.name}\n\n`;
    context += `Proof of Value Outcomes:\n\n`;

    guide.outcomes.forEach((outcome) => {
      context += `Outcome Title: ${outcome.title}\n`;
      if (outcome.description) {
        context += `Description: ${outcome.description}\n`;
      }
      context += `\n\nUse Cases:\n\n`;

      if (outcome.useCases.length === 0) {
        context += `\tNo use cases available for this outcome.\n`;
      } else {
        outcome.useCases.forEach((usecase) => {
          if (usecase.product === selectedProduct.product.id)
            context += `\tUse-Case Title: ${usecase.title}\n`;
          if (usecase.description) {
            context += `\tDescription: ${usecase.description}\n`;
          }
          context += `\tType: ${usecase.type}\n`;

          if (usecase.testCases.length === 0) {
            context += `\tNo test cases available for this use case.\n`;
          } else {
            context += `\tTest Cases:\n`;
            usecase.testCases.forEach((testCase) => {
              context += `\t\tTest Case Title: ${testCase.title}\n`;
              if (testCase.description) {
                context += `\t\tDescription: ${testCase.description}\n`;
              }
              context += `\t\tSuccess: ${testCase.success ? 'Yes' : 'No'}\n`;
              context += `\t\tValidation: ${testCase.validation ? testCase.validation: 'Unknown'}\n`;
            });
          }

          context += `\n`;
        });
      }

      context += `\n`;
    });
    setAiContextText(context);

    fetchOrCreateAIGuide(context);
  };
  return (
    <>
      <Typography variant="h6">Readout</Typography>
      <HbrDivider className="mb-4" />
      {aiLoading && <div><HbrSpinner  size="large">
        <div slot="label">Loading...</div></HbrSpinner></div>}
      {aiError && <div style={{ color: 'red' }}>{aiError}</div>}
      {aiResponse && !aiLoading && !aiError && (
        <AIContent
          related_type={'opportunity'}
          related_id={opportunityId}
          question_id={108}
          height={aiContentHeight}
          width={'100%'}
          conversation={aiResponse.conversation}
          context={aiContextText}
          conversationRequired={true}
          item={opportunity}
          pdfTitle={`${opportunity.Name}${productName} Validation Readout`}
        />
      )}
    </>
  )
}
export default ValidatePhaseReadout;